import axios from 'axios';
import { decodeHtmlEntities } from './tecCalendarConfig';

const DEFAULT_LOCATION = 'Surrey';
const WP_BASE = 'https://pacificrimsafety.ca';
const WC_V3_URL = `${WP_BASE}/wp-json/wc/v3/products`;
const WC_APPOINTMENTS_URL = `${WP_BASE}/wp-json/wc-appointments/v1/products`;

const splitCourseAndLocation = (name) => {
  const decoded = decodeHtmlEntities(name);
  const parts = decoded.split(/\s*[-–]\s*/);
  return {
    course: (parts[0] || decoded).trim(),
    location: (parts[1] || '').trim(),
  };
};

const getProductCourseName = (name) => splitCourseAndLocation(name).course;

const getProductLocation = (name) => splitCourseAndLocation(name).location;

export const normalizeCourseKey = (str) =>
  decodeHtmlEntities(str)
    .toLowerCase()
    .replace(/\(csa\/oh&s?\s*\)/gi, 'csa')
    .replace(/\(esc\)/gi, 'esc')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

const extractSlugFromUrl = (url) => {
  if (!url) return '';
  const match = String(url).match(/\/([^/?#]+)\/?(?:\?|#|$)/);
  return match ? match[1] : '';
};

const normalizeEventSlug = (slug) =>
  String(slug || '')
    .toLowerCase()
    .replace(/-\d+$/, '')
    .replace(/-surrey|-kelowna|-kamloops|-nanaimo|-vernon/g, '');

// TEC event titles/slugs → WooCommerce product slug patterns (order matters)
const COURSE_PRODUCT_RULES = [
  { event: /h2s\s*alive/i, slug: /h2s-alive/i },
  { event: /esc\s*confined/i, slug: /esc-confined-space/i },
  { event: /esc\s*fall/i, slug: /esc-fall-protection/i },
  { event: /fall\s*protection.*(csa|oh)/i, slug: /^fall-protection(?!.*esc)/i },
  { event: /fall\s*protection/i, slug: /fall-protection/i },
  { event: /confined\s*space.*(csa|oh)/i, slug: /confined-space-entry-monitor/i },
  { event: /confined\s*space/i, slug: /confined-space/i },
];

const EVENT_SLUG_PRODUCT_RULES = [
  { eventSlug: /^h2s-alive/, slug: /h2s-alive/i },
  { eventSlug: /^esc-confined-space/, slug: /esc-confined-space/i },
  { eventSlug: /^esc-fall-protection/, slug: /esc-fall-protection/i },
  { eventSlug: /^fall-protection-csa/, slug: /^fall-protection(?!.*esc)/i },
  { eventSlug: /^confined-space-csa/, slug: /confined-space-entry-monitor/i },
  { eventSlug: /^confined-space/, slug: /confined-space/i },
  { eventSlug: /^fall-protection/, slug: /fall-protection/i },
];

const getWooCredentials = () => {
  const key = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY;
  const secret = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_SECRET;
  if (!key || !secret || key.includes('your_consumer')) return null;
  return { consumer_key: key, consumer_secret: secret };
};

const normalizeProduct = (product) => ({
  id: product.id,
  name: product.name,
  slug: product.slug,
  permalink: product.permalink,
  type: product.type,
});

const mergeProducts = (target, incoming) => {
  for (const product of incoming) {
    if (!target.some((existing) => existing.id === product.id))
      target.push(normalizeProduct(product));
  }
};

export const fetchWooCommerceProducts = async () => {
  const creds = getWooCredentials();
  if (!creds) {
    console.error(
      'WooCommerce API keys missing. Add REACT_APP_WOOCOMMERCE_CONSUMER_KEY and REACT_APP_WOOCOMMERCE_CONSUMER_SECRET to client/.env, then rebuild (npm run build).'
    );
    return [];
  }

  const products = [];

  try {
    const { data } = await axios.get(WC_APPOINTMENTS_URL, {
      params: creds,
      timeout: 60000,
    });
    if (Array.isArray(data)) mergeProducts(products, data);
  } catch (err) {
    console.warn('WooCommerce appointments products failed:', err.response?.status || err.message);
  }

  let page = 1;
  while (true) {
    const { data } = await axios.get(WC_V3_URL, {
      params: { ...creds, per_page: 100, page, status: 'publish' },
      timeout: 60000,
    });
    if (!Array.isArray(data) || !data.length) break;

    mergeProducts(products, data);
    if (data.length < 100) break;
    page++;
  }

  if (products.length) console.log(`Loaded ${products.length} WooCommerce products`);
  else console.error('WooCommerce returned no products — check API key permissions (Read)');

  return products;
};

const pickByLocation = (candidates, location) => {
  if (!candidates.length) return null;

  const withLocation = candidates.find(
    (p) =>
      getProductLocation(p.name).toLowerCase() === location.toLowerCase() ||
      (p.slug && p.slug.toLowerCase().includes(location.toLowerCase()))
  );
  if (withLocation) return withLocation;

  const surrey = candidates.find(
    (p) => /surrey/i.test(p.name) || (p.slug && /surrey/i.test(p.slug))
  );
  if (surrey) return surrey;

  return candidates[0];
};

const matchByEventSlugRules = (products, eventSlugBase, location) => {
  for (const rule of EVENT_SLUG_PRODUCT_RULES) {
    if (!rule.eventSlug.test(eventSlugBase)) continue;
    const matches = products.filter((p) => rule.slug.test(p.slug || ''));
    const picked = pickByLocation(matches, location);
    if (picked) return picked;
  }
  return null;
};

export const findProductForEvent = (eventSlot, products) => {
  if (!products?.length) return null;

  const location = eventSlot.location || DEFAULT_LOCATION;
  const eventTitle = decodeHtmlEntities(eventSlot.product_name || '');
  const eventSlug = eventSlot.event_slug || extractSlugFromUrl(eventSlot.url);
  const eventSlugBase = normalizeEventSlug(eventSlug);

  if (eventSlugBase) {
    const byEventSlugRule = matchByEventSlugRules(products, eventSlugBase, location);
    if (byEventSlugRule) return byEventSlugRule;

    const slugMatches = products.filter((p) => {
      const productSlug = (p.slug || '').toLowerCase();
      const productSlugBase = productSlug.replace(/-surrey|-kelowna|-kamloops|-nanaimo|-vernon/g, '');
      return productSlug.includes(eventSlugBase) || eventSlugBase.includes(productSlugBase);
    });
    const bySlug = pickByLocation(slugMatches, location);
    if (bySlug) return bySlug;
  }

  for (const rule of COURSE_PRODUCT_RULES) {
    if (!rule.event.test(eventTitle)) continue;
    const matches = products.filter((p) => rule.slug.test(p.slug || ''));
    const picked = pickByLocation(matches, location);
    if (picked) return picked;
  }

  const eventKey = normalizeCourseKey(eventTitle);
  const titleMatches = products.filter((p) => {
    const productKey = normalizeCourseKey(getProductCourseName(p.name));
    return (
      productKey === eventKey ||
      productKey.includes(eventKey) ||
      eventKey.includes(productKey)
    );
  });

  return pickByLocation(titleMatches, location);
};

export const buildBookingUrl = (product, startDate) => {
  const dateStr = String(startDate || '').substring(0, 10);
  const timeMatch = String(startDate || '').match(/(\d{2}:\d{2})/);
  const timeStr = timeMatch ? timeMatch[1] : '08:30';
  let base = product.permalink || `${WP_BASE}/product/${product.id}/`;
  if (product.slug && !base.includes(product.slug))
    base = `${WP_BASE}/product/${product.id}/${product.slug}/`;
  if (!base.endsWith('/')) base += '/';
  return `${base}?date=${dateStr}&time=${timeStr}`;
};

export const attachProductsToEvents = (eventSlots, products) => {
  if (!products?.length) return eventSlots;

  return eventSlots.map((slot) => {
    const product = findProductForEvent(slot, products);
    if (!product) {
      console.warn(
        `No WooCommerce product matched: "${slot.product_name}" (slug: ${slot.event_slug || 'n/a'})`
      );
      return slot;
    }

    return {
      ...slot,
      event_id: slot.event_id || slot.product_id,
      woo_product_id: product.id,
      product_id: product.id,
      booking_url: buildBookingUrl(product, slot.date),
      product_slug: product.slug,
    };
  });
};

export const fetchProductsAndAttach = async (eventSlots) => {
  try {
    const products = await fetchWooCommerceProducts();
    if (!products.length) return eventSlots;
    return attachProductsToEvents(eventSlots, products);
  } catch (err) {
    console.warn('Could not load products for calendar links:', err.response?.status || err.message);
    return eventSlots;
  }
};
